import { useEffect, useState } from "react";
import { friendsApi, type Host } from "@/lib/api";
import { PageLoader, Button } from "@/components/ui";
import { Friends } from "@/screens/Friends";
import { Wallet } from "@/screens/Wallet";

type Boot = "loading" | "authed" | "anon";
type Tab = "friends" | "wallet";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function tgInitData(): Promise<string> {
  for (let i = 0; i < 20; i++) {
    const d = (window as { Telegram?: { WebApp?: { initData?: string } } }).Telegram?.WebApp?.initData;
    if (typeof d === "string" && d.length > 0) return d;
    if ((window as { Telegram?: { WebApp?: unknown } }).Telegram?.WebApp && i > 2) return "";
    await sleep(100);
  }
  return "";
}

async function bootstrap(): Promise<Host | null> {
  const initData = await tgInitData();
  if (initData) {
    try {
      return await friendsApi.telegram(initData);
    } catch {
      /* нет доступа/невалидно — попробуем cookie ниже */
    }
  }
  try {
    return await friendsApi.me();
  } catch {
    return null;
  }
}

export default function App() {
  const [boot, setBoot] = useState<Boot>("loading");
  const [host, setHost] = useState<Host | null>(null);
  const [tab, setTab] = useState<Tab>("friends");

  useEffect(() => {
    void bootstrap().then((h) => {
      setHost(h);
      setBoot(h ? "authed" : "anon");
    });
  }, []);

  if (boot === "loading") return <PageLoader />;
  if (boot === "anon" || !host) return <Login />;

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col">
      <header className="flex items-center justify-between px-4 pt-4">
        <div>
          <div className="text-lg font-bold tracking-tight">
            TT<span className="text-brand">EX</span> · друзья
          </div>
          {host.phone && <div className="text-xs text-white/40">{host.phone}</div>}
        </div>
        <button
          onClick={() => void friendsApi.logout().finally(() => setBoot("anon"))}
          className="text-xs text-white/40 hover:text-white/70"
        >
          Выйти
        </button>
      </header>
      <main className="flex-1 px-4 pb-24 pt-3">
        {tab === "friends" && <Friends />}
        {tab === "wallet" && <Wallet />}
      </main>
      <nav className="fixed inset-x-0 bottom-0 mx-auto max-w-md border-t border-white/5 bg-surface-base/95 backdrop-blur">
        <div className="grid grid-cols-2">
          {([
            { key: "friends", label: "Друзья", icon: "👥" },
            { key: "wallet", label: "Кошелёк", icon: "▤" },
          ] as { key: Tab; label: string; icon: string }[]).map((it) => (
            <button
              key={it.key}
              onClick={() => setTab(it.key)}
              className={`flex flex-col items-center gap-0.5 py-3 text-xs ${tab === it.key ? "text-brand" : "text-white/45"}`}
            >
              <span className="text-base leading-none">{it.icon}</span>
              {it.label}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}

function Login() {
  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center px-6 text-center">
      <div className="text-2xl font-bold tracking-tight">
        TT<span className="text-brand">EX</span> · кабинет друзей
      </div>
      <p className="mt-3 text-sm text-white/50">
        Откройте кабинет из Telegram-бота: команда{" "}
        <span className="font-mono text-white/80">/friends</span> → кнопка{" "}
        <span className="text-white/80">«Открыть кабинет друзей»</span>.
      </p>
      <Button variant="ghost" className="mt-6" onClick={() => window.location.reload()}>
        Обновить
      </Button>
    </div>
  );
}
